<template>
  <div class="main-container">
    <div class="terms-of-use-modal">
      <label for="terms-of-use-close-button" class="iw-visuallyhidden">Close</label>
      <button id="terms-of-use-close-button" class="terms-of-use-modal__close-button" @click="hideModal">
        <svg
          class="terms-of-use-modal__image svg-inline--fa"
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
        >
          <path
            fill="currentColor"
            stroke="currentColor"
            stroke-linecap="square"
            stroke-width="2"
            d="M1.368 1.368l13.264 13.264m0-13.264L1.368 14.632"
          />
        </svg>
      </button>
      <div class="terms-of-use-modal__text">
        <h2>Appetize Website Terms of Use</h2>
        <h3>(Rev. August 1, 2020)</h3>
        <p>
          Thank you for accessing
          <a href="https:\\www.appetizeapp.com">www.appetizeapp.com</a> or using our mobile ordering services (collectively, our
          <strong class="terms-of-use-modal__strong">“Sites”</strong>). Please read these terms and conditions (<strong
            class="terms-of-use-modal__strong"
            >“Terms”</strong
          >) before using our Sites, which are operated by Appetize Technologies, Inc. (<strong class="terms-of-use-modal__strong"
            >“Appetize,” “we” or “us”</strong
          >), with offices located at 6601 Center Drive West, Suite 700, Los Angeles, CA 90045.
        </p>
        <p>
          These Terms (together with any documents referred to in them) set forth for you the terms of use on which you make use
          of our Sites, whether as a guest or a registered user (<strong class="terms-of-use-modal__strong"
            >“user,” “you” or “your”</strong
          >). Use of our Sites includes accessing, browsing or registering on our Sites or using services that you access via our
          Sites. Please read these Terms carefully before you start to use our Sites. By using our Sites, you confirm that you
          accept these Terms and that you agree to comply with them. If you do not agree to these Terms, you must <u>not</u> use
          our Sites.
        </p>

        <h2 class="terms-of-use-modal__header">Other Applicable Terms</h2>
        <p>
          These Terms also refer to our Privacy Policy, which also applies to your use of our Sites. Specifically, our
          <strong class="terms-of-use-modal__strong">Privacy Policy</strong>, available at
          <a href="https://appetize.com/privacy-policy">https://appetize.com/privacy-policy</a>, sets out the terms on which we
          collect and process any Personal Information we collect from you, or that you provide to us. By using our Sites, you
          consent to such collection and processing practices and you warrant that all data provided by you is accurate.
        </p>

        <h2 class="terms-of-use-modal__header">Changes to These Terms</h2>
        <p>
          From time to time, we may modify these Terms. Please check this page regularly to take notice of any changes we make, as
          they are binding on you. Your use of our Sites following any such change constitutes your agreement to follow and be
          bound by these Terms as amended. If, at any time, you do not wish to accept these Terms, you may not use our Sites.
        </p>

        <h2 class="terms-of-use-modal__header">Changes to Our Sites</h2>
        <p>
          We may update our Sites from time to time. We do not guarantee that our Sites, or any changes made to them, will be free
          from errors or omissions, and you accept that such errors or omissions may result in your being unable to access our
          Sites.
        </p>
        <p>
          We may change the content on our Sites at any time. However, please note that any of the content on our Sites may be out
          of date at any given time, and we are under no obligation to update it. The content contained on our Sites is provided
          to you “as is” without representation or warranty of any kind.
        </p>

        <h2 class="terms-of-use-modal__header">Accessing Our Sites</h2>
        <p>
          We do not guarantee that our Sites, or any content thereon, will always be available or uninterrupted. Access to our
          Sites is permitted on a temporary basis. We may suspend, withdraw, discontinue or change all or any part of our Sites
          without notice. We will not be liable to you if for any reason our Sites are unavailable at any time or for any period.
        </p>
        <p>
          You are responsible for making all arrangements necessary for you to have access to our Sites. We do not warrant that
          our Sites are compatible with your computer equipment or mobile device or that our Sites or services s are free of
          errors or viruses, worms or “Trojan horses” and we are not liable for any damage you may suffer as a result of such
          destructive features.
        </p>
        <p>
          You are also responsible for ensuring that all persons who access our Sites through your Internet connection or mobile
          device are aware of these Terms and other applicable terms and conditions, and that they comply with them.
        </p>

        <h2 class="terms-of-use-modal__header">
          Acceptable Use Policy - Prohibited Uses
        </h2>
        <p>
          You may use our Sites only for lawful purposes. We grant you a limited, revocable, non-transferable right to use our
          Sites for your personal, noncommercial use. You obtain no other rights, interest or claim to our Sites or any aspect of
          our Sites at all. You must not use our Sites:
        </p>
        <ul style="{ list-style-type: circle; }">
          <li>
            In any way that is unlawful or fraudulent, or has any unlawful or fraudulent purpose or effect;
          </li>
          <li>
            For the purpose of harming or attempting to harm minors in any way;
          </li>
          <li>
            To infringe upon our intellectual property rights or those of others;
          </li>
          <li>To collect the personal information of others;</li>
          <li>
            To send, knowingly receive, upload, download, use or re-use any material which does not comply with our Code of
            Conduct below;
          </li>
          <li>
            To transmit, or procure the sending of, any unsolicited or unauthorized advertising or promotional material or any
            other form of similar solicitation (spam); and
          </li>
          <li>
            To knowingly transmit any data, send or upload any material that contains viruses, Trojan horses, worms, time bombs,
            keystroke loggers, spyware, adware or any other harmful programs or similar computer code designed to adversely affect
            the operation of any computer software or hardware.
          </li>
        </ul>
        <p>You also agree <u>not to</u>:</p>
        <ul style="{ list-style-type: circle; }">
          <li>
            Reproduce, duplicate, copy or re-sell any part of our Sites in contravention of the provisions of these Terms; or
          </li>
          <li>Access without authority, interfere with, damage or disrupt:</li>
          <ul style="{ list-style-type: circle; }">
            <li>Any part of our Sites;</li>
            <li>Any equipment or network on which our Sites are stored;</li>
            <li>Any software used in the provision of our Sites; or</li>
            <li>
              Any equipment or network or software owned or used by any third party.
            </li>
          </ul>
        </ul>
        <p>
          This list is non-exhaustive. We reserve the right to terminate your use of our Sites for violating these Terms
          including, without limitation, the prohibited uses.
        </p>

        <h2 class="terms-of-use-modal__header">Code of Conduct</h2>
        <p>
          We support the values of tolerance and respect of others. For this reason, by using these Sites, you agree
          <u>not to</u>:
        </p>
        <ul>
          <li>
            Publish or provide any data that may be considered “private” or “sensitive”;
          </li>
          <li>
            Convey any racist, sexist, violent, xenophobic, homophobic, malicious, rude, obscene or unlawful comments;
          </li>
          <li>
            Disseminate any content that may be harmful, defamatory, unauthorized, malicious or infringing on privacy or publicity
            rights, inciting violence, racial or ethnic hatred or qualify as gross indecency or incitement to commit certain
            crimes or offenses;
          </li>
          <li>
            Use the Sites for political, propaganda or proselytizing purposes;
          </li>
          <li>
            Publish any content advertising or promoting any products and/or services competing with the Appetize brand(s)
            displayed on the Sites;
          </li>
          <li>Divert the Sites from their intended purposes;</li>
          <li>
            Disseminate any information that may directly or indirectly allow the nominal and specific identification of an
            individual without that individuals’ prior and express consent, such as last name, mailing address, email address or
            telephone number;
          </li>
          <li>Disseminate any information or content that may be upsetting;</li>
          <li>Intimidate or harass others;</li>
          <li>
            Conduct illegal activities, including activities that may infringe anyone’s rights in and to any software, trademarks,
            photographs, images, texts, videos and the like;
          </li>
          <li>
            Disseminate content (including photographs and videos) portraying minors.
          </li>
        </ul>
        <p>
          WE RESERVE THE RIGHT TO LIMIT OR SUSPEND ACCESS TO ANY USER WHO (I) VIOLATES THIS CODE OF CONDUCT OR THESE TERMS AND
          (II) REFUSES TO CHANGE, EDIT OR DELETE ANY USER CONTENT THAT WE DEEM, IN OUR SOLE DISCRETION, TO VIOLATE THIS CODE OF
          CONDUCT OR THESE TERMS.
        </p>

        <h2 class="terms-of-use-modal__header">
          Acceptable Use Policy – Suspension and Termination
        </h2>
        <p>
          We will determine, in our discretion, whether there has been a breach of these Terms through your use of our Sites. When
          a breach of these Terms has occurred, we may take such action as we deem appropriate, and may result in our taking all
          or any of the following actions:
        </p>
        <ul>
          <li>Withdraw your right to use our Sites;</li>
          <li>Issue a warning to you;</li>
          <li>
            Commence legal proceedings against you for reimbursement of all costs on an indemnity basis (including, but not
            limited to, reasonable administrative and legal costs) resulting from the breach;
          </li>
          <li>Take further legal action against you; and</li>
          <li>
            Disclose such information to law enforcement authorities as we reasonably feel is necessary.
          </li>
        </ul>
        <p>
          We exclude liability for actions taken in response to breaches of these Terms. The responses described in these Terms
          are not limited, and we may take any other action we deem appropriate in our sole discretion.
        </p>

        <h2 class="terms-of-use-modal__header">Electronic Communication</h2>
        <p>
          When you use our Sites, register on our Sites or send email communications to us, you are communicating with us
          electronically. You consent to receive communications from us electronically. We will communicate with you by email or
          by posting notices on our Sites. You agree that all notices, disclosures or other communications that we provide to you
          electronically satisfy any legal requirement that such communications be in writing.
        </p>

        <h2 class="terms-of-use-modal__header">No Warranty</h2>
        <p>
          Our Sites are provided “as is” and exclude any warranties of any kind, either express or implied, to the fullest extent
          permissible pursuant to applicable law including, but not limited to, the exclusion of warranties of title,
          merchantability, satisfactory quality, fitness for a particular purpose and non-infringement of proprietary or third
          party rights.
        </p>

        <h2 class="terms-of-use-modal__header">Your Account and Password</h2>
        <p>
          If you are provided with a user identification code, password or any other piece of information as part of our security
          procedures, you must treat such information as confidential. You must not disclose it to any third party.
        </p>
        <p>
          We have the right to disable any user identification code or password, whether chosen by you or allocated by us, at any
          time, if in our reasonable opinion, you have failed to comply with any of the provisions of these Terms.
        </p>
        <p>
          If you know or suspect that anyone other than you knows your user identification code or password, you must promptly
          notify us at
          <a href="mailto: privacy@appetize.com">privacy@appetize.com</a>.
        </p>

        <h2 class="terms-of-use-modal__header">Intellectual Property</h2>
        <p>
          Except for our customers’ branding and/or content which is displayed on our Sites, we are the owner or the licensee of
          all intellectual property rights in our Sites, and in the material published on them including, but not limited to,
          text, content, software, video, music, sound, graphics, photographs, illustrations, artwork, photographs, names, logos,
          trademarks, service marks and other material (<strong class="terms-of-use-modal__strong">“Content”</strong>). The
          Content is protected by copyright laws and treaties around the world. All such rights are reserved.
        </p>
        <p>
          You acquire no rights or licenses in or to our Sites and/or the Content other than the limited right to use our Sites in
          accordance with these Terms and to download Content on the terms set out in this section. Other than as set out in this
          section, you may not copy, reproduce, recompile, decompile, disassemble, reverse engineer, distribute, publish, display,
          perform, modify, upload to create derivative works from, transmit or in any other way, exploit any part of our Sites.
        </p>
        <p>
          You agree to abide by all additional copyright notices or restrictions contained in our Sites.
        </p>
        <p>
          You agree to notify us promptly in writing if you become aware of any unauthorized access to or use of our Sites by any
          party or of any claim that our Sites or any of the contents of our Sites infringes any copyright, trademark or other
          contractual, statutory or common law rights of any party.
        </p>

        <h2 class="terms-of-use-modal__header">Limitation of Liability</h2>
        <p>
          To the extent permitted by law, we exclude all conditions, warranties, representations or other terms that may apply to
          our Site or any Content on it, whether express or implied.
        </p>
        <p>
          We will not be liable to any user for any loss or damage, whether in contract, tort (including negligence), breach of
          statutory duty, or otherwise, even if foreseeable, arising under or in connection with:
        </p>
        <ul>
          <li>use of, or inability to use, our Site; or</li>
          <li>use of or reliance on any Content displayed on our Site.</li>
        </ul>
        <p>
          We will not be liable for any loss or damage caused by a virus, distributed denial-of-service attack, or other
          technologically harmful material that may infect your computer equipment, computer programs, data or other proprietary
          material due to your use of our Site or to your downloading of any Content on it, or on any website linked to it.
        </p>

        <h2 class="terms-of-use-modal__header">Indemnity</h2>
        <p>
          You agree to indemnify, defend, and hold harmless each of Appetize, its employees, representatives and agents, from and
          against any claims, actions, demands or other proceedings brought against any of Appetize, its employees,
          representatives or agents, by a third party, to the extent that such claim, suit, action or other proceeding brought
          against Appetize its employees, representatives, suppliers or agents is based on or arises in connection with:
        </p>
        <ol type="i">
          <li>your use of our Sites;</li>
          <li>any breach by you of these Terms;</li>
          <li>a claim that any use of our Sites by you:</li>
          <ol type="a">
            <li>
              infringes any intellectual property rights of any third party, or any right of personality or publicity, or
            </li>
            <li>
              is libelous or defamatory, or otherwise results in injury or damage to any third party;
            </li>
          </ol>
          <li>
            any deletions, additions, insertions or alterations to, or any unauthorized use of, our Sites by you; or
          </li>
          <li>
            any misrepresentation or breach of representation or warranty made by you contained herein.
          </li>
        </ol>
        <p>
          References in this section of these Terms to your use of our Sites shall be deemed to include any use by a third party
          where such a third party accesses our Sites using your computer or mobile device. You agree to pay Appetize, its
          employees, representatives and agents any and all costs, damages and expenses (including reasonable attorneys’ fees)
          awarded against any of them or otherwise incurred by any of them in connection with or arising from any such third party
          claim, suit, action or proceeding attributable to any such third party claim.
        </p>

        <h2 class="terms-of-use-modal__header">Links</h2>
        <p>
          There may be links that will let you leave these Sites or that will let you access these Sites from third party sites.
          Linked third party sites are not under our control and we are not responsible for the contents of any such linked site
          or any link contained in such a linked site. We will not be liable for any loss or damage that may arise from your use
          of them. Any concerns regarding any such service or resource or any link thereto should be directed to the particular
          outside service or resource. Existence of links to other third party sites is not an endorsement in favor of such site
          or the products or services contained in any linked site.
        </p>

        <h2 class="terms-of-use-modal__header">Entire Agreement</h2>
        <p>
          Except as provided herein, these Terms and any other terms of use or policies referred to herein are the entire
          agreement between us and supersede any prior understanding or agreements (written or oral).
        </p>

        <h2 class="terms-of-use-modal__header">
          Governing Law and Jurisdiction
        </h2>
        <p>
          These Terms shall be construed in accordance with the laws of the State of California, without regard to its choice of
          laws principles. You hereby consent to the personal jurisdiction of the state and Federal courts in the State of
          California and agree that any action related to these Terms must be brought in a state or Federal court located in Los
          Angeles County, CA and waive any objection that may exist, now or in the future, with respect to any of the foregoing,
          including a claim of forum non conveniens.
        </p>

        <h2 class="terms-of-use-modal__header">Local Laws and Regulations</h2>
        <p>
          Our Sites are not directed at any person in any jurisdiction where for any reason the publication or availability of our
          Sites is prohibited. Those in respect of whom such prohibitions apply must not access our Sites.
        </p>
        <p>
          We do not represent that either our Sites or the Content are appropriate for use or permitted by local laws in all
          jurisdictions. Those who access our Sites do so on their own initiative and are responsible for compliance with
          applicable local laws or regulations; you should seek legal advice in case of doubt.
        </p>

        <h2 class="terms-of-use-modal__header">Miscellaneous</h2>
        <p>
          No waiver by either party of any breach or default hereunder shall be deemed to be a waiver of any preceding or
          subsequent breach or default. If any provision of these Terms is held to be invalid, void or for any reason
          unenforceable, such provision will be deemed replaced with a valid and enforceable provision most closely reflecting the
          intent and purpose of the original provision. The parties have agreed that these Terms and any of its accessories be
          written in the English language. Les parties aux présentes ont exigé que ce contrat et ses accessoires, y compris tout
          avis, soient rédigés en anglais. Both the English and French versions of these Terms have equal legal value.
        </p>

        <h2 class="terms-of-use-modal__header">Contact Us</h2>
        <p>To contact us, please:</p>
        <p>
          Write to us at the following address: 6601 Center Drive West, Suite 700, Los Angeles, CA 90045
        </p>
        <p>Call our helpline at 1.877.559.4225.</p>
        <p>
          Email us at
          <a href="mailto: privacy@appetize.com">privacy@appetize.com</a> or
          <a href="mailto:marketing@appetize.com">marketing@appetize.com</a>.
        </p>
        <p>Thank you for visiting our Sites.</p>
        <p>
          © Copyright 2020 Appetize Technologies, Inc. Appetize and the Appetize logo are registered trademarks of Appetize
          Technologies, Inc. All rights reserved.
        </p>
        <p>Last updated: As of August 1, 2020</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "TermsOfUseModal",
  methods: {
    hideModal() {
      this.$modal.hide("TermsOfUseModal");
    }
  }
};
</script>
<style lang="scss" scoped>
.dark {
  .terms-of-use-modal {
    color: $primary-color;
  }
  a:link {
    color: #ffffff;
  }
  a:visited {
    color: #ffffff;
  }
}

.main-container {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.terms-of-use-modal {
  display: flex;
  justify-content: left;
  align-items: flex-start;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  height: 100%;
  font-family: $theme2__fontFamilyPrimary;
  color: $text-color;

  &__text {
    display: block;
    width: 100%;
    font-size: 20px;
    font-weight: 100;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.65;
    letter-spacing: normal;
    margin-top: 85px;
    margin-bottom: 85px;
    margin-left: 85px;
    margin-right: 85px;

    @include respond-to-mobile {
      margin-left: 27px;
      margin-right: 27px;
      font-size: 14px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.5;
      letter-spacing: normal;
    }
  }

  &__close-button {
    all: unset;
    cursor: pointer;
    position: absolute;
    top: 20px;
    right: 20px;

    @include respond-to-mobile {
      top: 7px;
      right: 7px;
    }

    img {
      width: 20px;
      height: 20px;
    }
  }

  &__close-button:focus {
    outline: orange 5px auto;
  }

  &__header {
    margin-top: 50px;
  }
  &__strong {
    font-weight: bold;
  }
}

li {
  margin-top: 15px;
  padding-left: 20px;
}
a {
  opacity: 0.7;
  &:hover {
    text-decoration: underline;
  }
}
</style>
